// deps
import React, { ReactElement, memo, useMemo } from "react";
import classNames from "classnames";

// internal-deps
import { Tick } from "@swiggy-private/core-ui";

// interfaces
import { CardPaymentMethodData } from "@payment-ui/interfaces/Card";
import { getCardMethodTestIds, isFoodCard } from "@payment-ui/helpers/Card";

// widgets
import CircleLoader from "@payment-ui/widgets/circle-loader";

import styles from "../Card.v4.scss";

interface CardBalanceProps {
    /** weather the method is selected or not, required to show tick */
    selected: boolean;
    /** contains method data card informations */
    methodData: CardPaymentMethodData;
    hasLessBalance: boolean;
    isBalanceLoading: boolean;
    cardBalance: string | number;
}

function CardBalance(props: CardBalanceProps): ReactElement {
    const { hasLessBalance, isBalanceLoading, cardBalance, methodData } = props;
    // check if the given card is a food card
    const foodCard = useMemo(() => isFoodCard(methodData), [methodData]);

    const balanceValueClass = useMemo(
        () =>
            classNames({
                [styles.paymentMethodCardBalanceContainerValue]: true,
                [styles.paymentMethodCardBalanceContainerValueLow]: hasLessBalance,
            }),
        [hasLessBalance],
    );

    const cardBalanceClass = classNames(styles.paymentMethodCardBalance, {
        [styles.paymentMethodCardBalanceWithSubText]: hasLessBalance || methodData?.meta?.promoMsg,
    });

    const testIds = useMemo(() => getCardMethodTestIds(methodData), [methodData]);

    const balance = useMemo((): ReactElement => {
        if (isBalanceLoading) {
            return <CircleLoader size="medium" data-testid={testIds.CIRCLE_LOADER} />;
        }

        return <div className={balanceValueClass}>{cardBalance}</div>;
    }, [isBalanceLoading, balanceValueClass, cardBalance, testIds.CIRCLE_LOADER]);

    return (
        <div className={cardBalanceClass}>
            <div className={styles.paymentMethodCardBalanceContainer} data-testid={testIds.BALANCE}>
                {foodCard && balance}
                {!isBalanceLoading && !hasLessBalance && <Tick selected={props.selected} data-testid={testIds.TICK} />}
            </div>
        </div>
    );
}

export default memo(CardBalance);
